import {SerializedStyles} from '@emotion/react';
import {Chip, ChipProps, css, Typography, useTheme} from '@mui/material';
import React from 'react';
import {useIntl} from 'react-intl';
import {GetStylesFunction} from 'src/js/common/types';
import {TranslateUtils} from 'src/js/common/utils';
import {useMuiAnchor} from 'src/js/hooks/useMuiAnchor';

import {DlMuiPopper} from './DlMuiPopper';

interface OwnProps extends ChipProps {
  inputCss?: SerializedStyles;
  title?: string;
  label: string;
  whiteText?: boolean;
  leftIcon?: JSX.Element;
}

type Props = OwnProps;

export const DlMuiChip: React.FC<Props> = (props) => {
  const {
    inputCss,
    title,
    label,
    size = 'small',
    whiteText,
    leftIcon,
    ...restProps
  } = props;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({theme, params: {inputCss, whiteText}});

  const translatedLabel = intl && TranslateUtils.translate(label, intl);
  const translatedTitle =
    intl && title && TranslateUtils.translate(title, intl);

  const hasTooltip = !!translatedTitle && translatedLabel !== '';

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} =
    useMuiAnchor(hasTooltip);

  return (
    <>
      <Chip
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        data-name={'dl-mui-chip--chip'}
        variant={'filled'}
        size={size}
        label={translatedLabel}
        icon={leftIcon}
        {...restProps}
        css={styles.chip}
      />
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{translatedTitle}</Typography>
      </DlMuiPopper>
    </>
  );
};

DlMuiChip.displayName = 'DlMuiChip';

const getStyles: GetStylesFunction = (options) => ({
  chip: css(
    {
      height: '18px',
      borderRadius: '2px',
      border: 'none',
      backgroundColor: 'var(--dlTheme-50)',
      position: 'relative',
      zIndex: 0,
      justifyContent: 'space-between',
      '& .MuiChip-label': {
        color: options?.params?.whiteText ? '#fff' : 'var(--dlTheme-800)',
      },
      '& .MuiChip-icon': {
        color: '#121619',
      },
      '& .MuiChip-deleteIcon': {
        margin: 0,
        borderTopRightRadius: '2px',
        borderBottomRightRadius: '2px',
        color: 'var(--dlThemeGray-900)',
        '&:hover': {
          color: 'var(--dlThemeGray-900)',
          backgroundColor: 'var(--dlTheme-100)',
        },
      },
    },
    options?.params?.inputCss
  ),
});
